// Bid Pack — superset of the CSDB Seed Pack that also includes
// Risk register, Schedule plan (phases + monthly grid) and Bid pricing build-up.
import { _buildSeedPackZip, safeFilename, triggerDownload } from "./seedPack";
import {
  RISK_CATEGORIES,
  RISK_LIKELIHOOD,
  RISK_IMPACT,
  SCHEDULE_PHASES,
} from "./s1000dData";

// ---- CSV helpers ----
function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function toCsv(rows) {
  return rows.map((r) => r.map(csvEscape).join(",")).join("\n");
}

// ---- Risk register ----
function buildRiskRegisterCsv(risk) {
  const catLabel = (id) => RISK_CATEGORIES.find((c) => c.id === id)?.label || id;
  const lLabel = (v) => RISK_LIKELIHOOD.find((l) => l.v === v)?.label || "";
  const iLabel = (v) => RISK_IMPACT.find((l) => l.v === v)?.label || "";
  const header = ["#", "Title", "Category", "Likelihood", "L-label", "Impact", "I-label", "Score", "Band", "Mitigation", "Owner"];
  const rows = [header];
  risk.risks.forEach((r, idx) => {
    rows.push([
      idx + 1, r.title, catLabel(r.category),
      r.likelihood, lLabel(r.likelihood),
      r.impact, iLabel(r.impact),
      r.score, r.band.toUpperCase(),
      r.mitigation || "", r.ownerRole || "",
    ]);
  });
  return toCsv(rows);
}

function buildRiskRegisterMarkdown(risk, riskPlan) {
  const lines = [];
  lines.push(`# Risk & Assumptions Register`);
  lines.push("");
  lines.push(`**Risks logged:** ${risk.count} · CRIT ${risk.breakdown.critical} · HIGH ${risk.breakdown.high} · MED ${risk.breakdown.medium} · LOW ${risk.breakdown.low}`);
  lines.push(`**Max score:** ${risk.maxScore}/25 · **Exposure:** ${(risk.exposure * 100).toFixed(0)}%`);
  lines.push(`**Auto-bump contingency:** ${risk.autoContingency ? "ON" : "OFF"} · suggested uplift: +${risk.suggestedContingencyBump.toFixed(1)}%`);
  lines.push("");
  lines.push(`## Risks`);
  if (risk.risks.length === 0) {
    lines.push(`_No risks logged._`);
  } else {
    lines.push("| # | Title | Category | L | I | Score | Band | Owner | Mitigation |");
    lines.push("|---|-------|----------|---|---|-------|------|-------|------------|");
    risk.risks.forEach((r, i) => {
      lines.push(`| ${i + 1} | ${r.title} | ${r.category} | ${r.likelihood} | ${r.impact} | ${r.score} | ${r.band.toUpperCase()} | ${r.ownerRole || ""} | ${r.mitigation || ""} |`);
    });
  }
  lines.push("");
  lines.push(`## Assumptions`);
  if (!riskPlan?.assumptions?.length) {
    lines.push("_No assumptions documented._");
  } else {
    riskPlan.assumptions.forEach((a, i) => lines.push(`${i + 1}. ${a.text}`));
  }
  lines.push("");
  lines.push(`## Dependencies`);
  if (!riskPlan?.dependencies?.length) {
    lines.push("_No dependencies documented._");
  } else {
    riskPlan.dependencies.forEach((d) => lines.push(`- **${d.type}** — ${d.text}`));
  }
  return lines.join("\n");
}

// ---- Schedule ----
function buildSchedulePhasesCsv(schedule) {
  const header = ["Phase", "Percent", "Hours", "StartOffsetWeeks", "DurationWeeks"];
  const rows = [header];
  schedule.phases.forEach((p) => {
    rows.push([p.label, p.percent, p.hours.toFixed(1), p.startOffsetWeeks, p.durationWeeks]);
  });
  rows.push(["TOTAL", schedule.totalPercent.toFixed(1), schedule.totalAllocated.toFixed(1), "", ""]);
  return toCsv(rows);
}

function buildScheduleMonthlyCsv(schedule) {
  const header = ["Month", ...SCHEDULE_PHASES.map((p) => p.label), "Total"];
  const rows = [header];
  schedule.months.forEach((m) => {
    const row = [m.label];
    SCHEDULE_PHASES.forEach((p) => row.push((m.byPhase[p.key] || 0).toFixed(1)));
    row.push(m.totalHours.toFixed(1));
    rows.push(row);
  });
  return toCsv(rows);
}

function buildScheduleMarkdown(schedule, estimate) {
  const lines = [];
  lines.push(`# Schedule & Phase Plan`);
  lines.push("");
  lines.push(`**Total allocated:** ${schedule.totalAllocated.toFixed(1)} hrs · **% allocated:** ${schedule.totalPercent.toFixed(1)}%`);
  if (estimate.resourcePlan?.startDate) lines.push(`**Project start:** ${estimate.resourcePlan.startDate}`);
  if (estimate.resourcePlan?.endDate) lines.push(`**Project end:** ${estimate.resourcePlan.endDate}`);
  lines.push(`**Basis:** ${estimate.schedulePlan?.basisHours || "combined"}`);
  lines.push("");
  lines.push(`## Phase mix`);
  lines.push("| Phase | % | Hours | Start (week) | Duration (weeks) |");
  lines.push("|-------|---|-------|--------------|------------------|");
  schedule.phases.forEach((p) => {
    lines.push(`| ${p.label} | ${p.percent}% | ${p.hours.toFixed(1)} | ${p.startOffsetWeeks} | ${p.durationWeeks} |`);
  });
  if (schedule.months.length > 0) {
    lines.push("");
    lines.push(`## Monthly loading`);
    lines.push(`| Month | ${SCHEDULE_PHASES.map((p) => p.label).join(" | ")} | Total |`);
    lines.push(`|-------|${SCHEDULE_PHASES.map(() => "------").join("|")}|-------|`);
    schedule.months.forEach((m) => {
      const cells = SCHEDULE_PHASES.map((p) => (m.byPhase[p.key] || 0).toFixed(1)).join(" | ");
      lines.push(`| ${m.label} | ${cells} | ${m.totalHours.toFixed(1)} |`);
    });
  }
  return lines.join("\n");
}

// ---- Bid pricing ----
function buildBidPricingMarkdown(bidPrice, estimate) {
  const sym = estimate.currency?.symbol || "$";
  const fc = (v) => `${sym}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const lines = [];
  lines.push(`# Bid Pricing — Price-to-Win Build-up`);
  lines.push("");
  lines.push(`**Cost basis:** ${estimate.bidPlan?.pricingBasis || "combined"}`);
  lines.push(`**Currency:** ${estimate.currency?.code || "USD"}`);
  lines.push("");
  lines.push("| Line | Value |");
  lines.push("|------|-------|");
  lines.push(`| Estimated cost | ${fc(bidPrice.cost)} |`);
  lines.push(`| Target margin | ${bidPrice.margin}% |`);
  lines.push(`| Margin amount | ${fc(bidPrice.marginAmount)} |`);
  lines.push(`| Target price (cost ÷ (1 − margin)) | ${fc(bidPrice.targetPrice)} |`);
  lines.push(`| Strategic adjustment | ${bidPrice.strategicAdj >= 0 ? "+" : ""}${bidPrice.strategicAdj}% |`);
  lines.push(`| **Final bid price** | **${fc(bidPrice.adjustedPrice)}** |`);
  lines.push(`| Effective margin | ${bidPrice.adjustedMargin.toFixed(1)}% |`);
  lines.push(`| Win probability | ${bidPrice.winProb}% |`);
  lines.push(`| Expected value (price × win-prob) | ${fc(bidPrice.expectedValue)} |`);
  lines.push(`| Expected margin contribution | ${fc(bidPrice.expectedMargin)} |`);
  if (bidPrice.competitor > 0) {
    lines.push(`| Competitor price | ${fc(bidPrice.competitor)} |`);
    lines.push(`| Delta vs competitor | ${bidPrice.competitorDelta >= 0 ? "+" : ""}${bidPrice.competitorDelta?.toFixed(1)}% |`);
  }
  lines.push("");
  lines.push(`## Recommendation`);
  lines.push(`**${bidPrice.recommendationLabel}**`);
  return lines.join("\n");
}

function buildBidPricingCsv(bidPrice) {
  const header = ["Line", "Value"];
  const rows = [header,
    ["Estimated cost", bidPrice.cost.toFixed(2)],
    ["Target margin %", bidPrice.margin],
    ["Margin amount", bidPrice.marginAmount.toFixed(2)],
    ["Target price", bidPrice.targetPrice.toFixed(2)],
    ["Strategic adjustment %", bidPrice.strategicAdj],
    ["Final bid price", bidPrice.adjustedPrice.toFixed(2)],
    ["Effective margin %", bidPrice.adjustedMargin.toFixed(2)],
    ["Win probability %", bidPrice.winProb],
    ["Expected value", bidPrice.expectedValue.toFixed(2)],
    ["Expected margin contribution", bidPrice.expectedMargin.toFixed(2)],
    ["Competitor price", bidPrice.competitor.toFixed(2)],
    ["Delta vs competitor %", bidPrice.competitorDelta == null ? "" : bidPrice.competitorDelta.toFixed(2)],
    ["Recommendation", bidPrice.recommendationLabel],
  ];
  return toCsv(rows);
}

// ---- Bid Pack composer ----
function _composeBidPackZip({ estimate, totals, resourcing, brex, dml, readiness, risk, schedule, bidPrice }) {
  // Start from the existing CSDB seed pack
  const { zip, manifest, base, generated } = _buildSeedPackZip({ estimate, totals, resourcing, brex, dml, readiness });

  // Risk register
  zip.file(`${base}_RiskRegister.md`, buildRiskRegisterMarkdown(risk, estimate.riskPlan));
  zip.file(`${base}_RiskRegister.csv`, buildRiskRegisterCsv(risk));

  // Schedule
  zip.file(`${base}_Schedule.md`, buildScheduleMarkdown(schedule, estimate));
  zip.file(`${base}_Schedule_phases.csv`, buildSchedulePhasesCsv(schedule));
  if (schedule.months.length > 0) {
    zip.file(`${base}_Schedule_monthly.csv`, buildScheduleMonthlyCsv(schedule));
  }

  // Bid pricing
  zip.file(`${base}_BidPricing.md`, buildBidPricingMarkdown(bidPrice, estimate));
  zip.file(`${base}_BidPricing.csv`, buildBidPricingCsv(bidPrice));

  // Combined manifest
  const sym = estimate.currency?.symbol || "$";
  const bidManifest = {
    ...manifest,
    name: "S1000D Bid Pack",
    generated,
    bid: {
      finalPrice: Number(bidPrice.adjustedPrice.toFixed(2)),
      currency: estimate.currency?.code || "USD",
      currencySymbol: sym,
      effectiveMarginPercent: Number(bidPrice.adjustedMargin.toFixed(2)),
      winProbabilityPercent: bidPrice.winProb,
      expectedValue: Number(bidPrice.expectedValue.toFixed(2)),
      recommendation: bidPrice.recommendation,
      recommendationLabel: bidPrice.recommendationLabel,
    },
    risk: {
      count: risk.count,
      critical: risk.breakdown.critical,
      high: risk.breakdown.high,
      medium: risk.breakdown.medium,
      low: risk.breakdown.low,
      maxScore: risk.maxScore,
      exposurePercent: Number((risk.exposure * 100).toFixed(1)),
      suggestedContingencyBumpPercent: risk.suggestedContingencyBump,
      autoContingency: risk.autoContingency,
      assumptionsCount: risk.assumptionsCount,
      dependenciesCount: risk.dependenciesCount,
    },
    schedule: {
      basisHours: estimate.schedulePlan?.basisHours || "combined",
      totalAllocatedHours: Number(schedule.totalAllocated.toFixed(1)),
      totalPercent: Number(schedule.totalPercent.toFixed(1)),
      monthCount: schedule.months.length,
      hasDates: schedule.hasDates,
    },
    files: [
      ...manifest.files,
      `${base}_RiskRegister.md`,
      `${base}_RiskRegister.csv`,
      `${base}_Schedule.md`,
      `${base}_Schedule_phases.csv`,
      ...(schedule.months.length > 0 ? [`${base}_Schedule_monthly.csv`] : []),
      `${base}_BidPricing.md`,
      `${base}_BidPricing.csv`,
    ],
  };
  zip.file("manifest.json", JSON.stringify(bidManifest, null, 2));

  return { zip, manifest: bidManifest, base, filename: `${base}_Bid_pack.zip` };
}

export async function exportBidPack(payload) {
  const { zip, manifest, filename } = _composeBidPackZip(payload);
  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, filename);
  return manifest;
}

export async function buildBidPackBlob(payload) {
  const { zip, manifest, filename } = _composeBidPackZip(payload);
  const blob = await zip.generateAsync({ type: "blob" });
  return { blob, filename, manifest };
}
